import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { PersonUtils } from 'src/app/auth_profile/utils/v3/person-utils';
import { Dates, TimeUnits } from 'src/app/utils/calendar-utils';
import { ModalComponent } from 'src/app/utils/modal/modal.component';
import { Pageable, Page } from 'src/app/utils/pageable';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import { LessonType } from '../../../model/calendar';
import { AppEventsService } from '../../../services/app-events.service';
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import * as LessonsV3 from "../../../model/v3/lessons";
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-student-lessons',
    templateUrl: './student-lessons.component.html',
    styleUrls: ['./student-lessons.component.scss'],
    standalone: false
})
export class StudentLessonsComponent implements OnInit, OnDestroy {
  // The boundary alone decides which card a lesson goes to, so both ask for the same
  // statuses. Initializing is the only one left out: those are unfinished reservation drafts
  // that the student side never showed.
  private readonly visibleStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
    LessonsV3.LessonStatus.Complete,
    LessonsV3.LessonStatus.Cancel,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];
  private _lessonType: LessonType;
  private _langCode: string;
  pageable: Pageable;
  lessons: Page<LessonsV3.LFStudentGeneral>;
  _studentId: number = null;
  isLoading = true;
  private teacherPhotoByPersonId = new Map<number, string>();
  private loadingTeacherPhotoIds = new Set<number>();

  @Input()
  set studentId(value: number) {
    this._studentId = value;
    this.reloadCurrent();
  }
  get studentId() {
    return this._studentId;
  }

  @Input()
  public set lessonType(lessonType: LessonType) {
    this._lessonType = lessonType;
    this.updatePageableSortByLessonType();
    this.reloadCurrent();
  }
  get lessonType() {
    return this._lessonType;
  }

  @Input()
  public set langCode(langCode: string) {
    this._langCode = langCode;
    this.reloadCurrent()
  }
  get langCode() {
    return this._langCode;
  }

  // cancel lesson
  @ViewChild('cancelLessonModal', { static: true })
  cancelLessonModal: ModalComponent;
  _clLessonToCancel: LessonsV3.LFStudentGeneral;
  _clReason: string;
  readonly cancelReasonMaxLength = 250;

  constructor(
    private spinner: SpinnerService,
    private nativeApiProvider: NativeServiceApiProvider,
    private router: Router,
    private appEvents: AppEventsService,
    private route: ActivatedRoute
  ) {
    this.pageable = new Pageable(0, 5, [
      'metric.details.plannedStartDate,DESC',
    ]);
  }

  lessonUpdatedSubscription: Subscription;

  ngOnInit(): void {
    if (this._lessonType === LessonType.Upcoming) {
      this.lessonUpdatedSubscription = this.appEvents.scheduleReserved.subscribe(() => {
        this.reloadCurrent();
      });
    }
  }

  ngOnDestroy(): void {
    this.lessonUpdatedSubscription?.unsubscribe();
  }

  public hasResults(): boolean {
    return !!this.lessons;
  }

  public getTotalResults(): number {
    if (!this.lessons) return 0;
    return this.lessons.totalElements;
  }

  private reloadCurrent() {
    this.isLoading = true;
    this.getLessons(this.pageable).subscribe();
  }

  private updatePageableSortByLessonType(): void {
    const sort = this._lessonType === LessonType.Upcoming
      ? ['metric.details.plannedStartDate,ASC']
      : ['metric.details.plannedStartDate,DESC'];
    this.pageable = new Pageable(this.pageable?.page ?? 0, this.pageable?.size ?? 5, sort);
  }

  // As in the legacy student endpoints, the status list is the same for both cards and the
  // date alone decides which card a lesson goes to. The boundary sits 5 minutes back so a
  // lesson that has just started still counts as upcoming - the lessons page lists the
  // future ones from the same boundary, so nothing is shown twice. Anything that is not
  // Upcoming is treated as Past, so no lesson type can produce an unbounded query.
  private getLessonFilters(): {
    statuses: LessonsV3.LessonStatus[];
    dateFrom: Date | null;
    dateTo: Date | null;
  } {
    const boundary = new Date(Date.now() - 5 * 60 * 1000);
    const isUpcoming = this.lessonType === LessonType.Upcoming;
    return {
      statuses: this.visibleStatuses,
      dateFrom: isUpcoming ? boundary : null,
      dateTo: isUpcoming ? null : boundary,
    };
  }

  getLessons(pageable: Pageable): Observable<Page<LessonsV3.LFStudentGeneral>> {
    if (
      this.lessonType === null ||
      this.lessonType === undefined ||
      !this.studentId ||
      this.langCode == undefined
    )
      return of(null as Page<LessonsV3.LFStudentGeneral>);
    this.pageable = pageable;
    const { statuses, dateFrom, dateTo } = this.getLessonFilters();
    return this.spinner.trace(
      this.nativeApiProvider.colV3().pipe(
        switchMap((api) =>
          api.findLessons(
            LessonsV3.LessonCategory.Col,
            null,
            'student-full',
            null,
            this.studentId,
            null,
            null,
            null,
            this.langCode,
            statuses,
            dateFrom,
            dateTo,
            this.pageable
          )
        ),
        map((lessons) => this.asStudentGeneralPage(lessons)),
        tap((lessons) => {
          this.lessons = lessons;
          this.preloadTeacherPhotos(this.lessons?.content ?? []);
        }),
        tap((_) => (this.isLoading = false))
      )
    );
  }

  private asStudentGeneralPage(
    lessons: Page<LessonsV3.LessonView>
  ): Page<LessonsV3.LFStudentGeneral> {
    const content = lessons?.content ?? [];
    const isStudentGeneral = content.every((lesson) =>
      lesson &&
      'students' in lesson &&
      'message' in lesson &&
      'flags' in lesson &&
      'teacher' in lesson
    );
    if (isStudentGeneral) {
      return lessons as Page<LessonsV3.LFStudentGeneral>;
    }
    throw new Error('Invalid lesson view type. Expected student-full payload.');
  }

  getLessonsToDisplay() {
    return this.lessons?.content ?? [];
  }

  public getLessonTeacherPhotoUrl(lesson: LessonsV3.LFStudentGeneral): string {
    const personId = lesson?.teacher?.reference?.person?.id;
    if (!personId) {
      return '';
    }
    return this.teacherPhotoByPersonId.get(personId) ?? '';
  }

  public getLessonTeacher(lesson: LessonsV3.LFStudentGeneral): string {
    const person = lesson?.teacher?.reference?.person;
    if (!person) {
      return '';
    }
    return PersonUtils.getPersonName(person);
  }

  getLessonStatus(lesson: LessonsV3.LFStudentGeneral) {
    return `COL.LESSONS.DETAILS.STATUS_DESCRIPTIONS.${lesson.status.toUpperCase()}`;
  }

  public getLessonDate(lesson: LessonsV3.LFStudentGeneral): Date {
    return lesson.lessonMetric.plannedStartDate;
  }

  public lessonBadgeClass(lesson: LessonsV3.LFStudentGeneral) {
    return this._lessonType === LessonType.Upcoming ||
      this._lessonType === LessonType.Past
      ? this.getLessonBadgeClassByStatus(lesson.status)
      : '';
  }

  openDetails(lesson: LessonsV3.LFStudentGeneral) {
    this.router.navigate(['lesson', lesson.id], {
      relativeTo: this.route,
    }).then();
  }

  public mayBeCancelled(lesson: LessonsV3.LFStudentGeneral) {
    if (
      this._lessonType === undefined ||
      this._lessonType !== LessonType.Upcoming
    ) {
      return false;
    }
    const lessonStatus = lesson.status;
    const timeDiff = Dates.diff(
      new Date(),
      lesson.lessonMetric.plannedStartDate
    );
    const moreThanMinimumCancellationPeriod =
      timeDiff > TimeUnits.Hours(12).toMilis();
    return (
      (lessonStatus === LessonsV3.LessonStatus.Booked ||
        lessonStatus === LessonsV3.LessonStatus.Due) &&
      moreThanMinimumCancellationPeriod
    );
  }

  public showLessonCancellation(lesson: LessonsV3.LFStudentGeneral) {
    this._clLessonToCancel = lesson;
    this._clReason = null;
    this.cancelLessonModal.show();
  }

  getLessonTypeName() {
    return this._lessonType === LessonType.Upcoming
      ? 'COL.LESSONS.LIST.FUTURE_LESSONS'
      : 'COL.LESSONS.LIST.PAST_LESSONS';
  }

  getInitials(person: LessonsV3.PersonDetailedRef) {
    return person ? PersonUtils.getInitials(person) : '';
  }

  public cancelLesson() {
    if (!this._clLessonToCancel) {
      this.cancelLessonModal.hide();
      return;
    }
    const lessonId = this._clLessonToCancel.id;
    const reason = this._clReason;
    const studentId = this.studentId;
    const action = this._clLessonToCancel.status === LessonsV3.LessonStatus.Due
      ? LessonsV3.LessonStatusAction.Invalidate
      : LessonsV3.LessonStatusAction.Cancel;
    const observable: Observable<LessonsV3.LessonView | void> = this.nativeApiProvider.colV3().pipe(
      switchMap((api) => api.updateLessonStatus(lessonId, action, { reason }, studentId))
    );
    observable
      .pipe(
        tap((_) => {
          this.appEvents.creditsUpdate.next(null);
          this._clLessonToCancel = null;
        }),
        switchMap((_) => this.getLessons(this.pageable))
      )
      .subscribe();
    this.cancelLessonModal.hide();
  }
  getLessonPlannedDate(lesson: LessonsV3.LFStudentGeneral) {
    return lesson.lessonMetric.plannedStartDate;
  }
  public getCancellationTeacher(lesson: LessonsV3.LFStudentGeneral) {
    return { teacher: this.getLessonTeacher(lesson) };
  }

  private getLessonBadgeClassByStatus(status: LessonsV3.LessonStatus): string {
    if (status === LessonsV3.LessonStatus.Complete) return 'bg-success';
    if (status === LessonsV3.LessonStatus.Cancel ||
      status === LessonsV3.LessonStatus.InvalidatedByStudent ||
      status === LessonsV3.LessonStatus.InvalidatedByTeacher) return 'bg-danger';
    if (status === LessonsV3.LessonStatus.InProgress) return 'bg-warning';
    return 'bg-primary';
  }

  private loadTeacherPhoto(personId: number): void {
    if (this.loadingTeacherPhotoIds.has(personId)) {
      return;
    }
    this.loadingTeacherPhotoIds.add(personId);
    this.nativeApiProvider.casa().pipe(
      switchMap((api) => api.getPhoto(personId)),
      take(1)
    ).subscribe({
      next: (photo) => {
        this.teacherPhotoByPersonId.set(personId, photo?.publicAccessUrl ?? '');
        this.loadingTeacherPhotoIds.delete(personId);
      },
      error: () => {
        this.teacherPhotoByPersonId.set(personId, '');
        this.loadingTeacherPhotoIds.delete(personId);
      }
    });
  }

  private preloadTeacherPhotos(lessons: LessonsV3.LFStudentGeneral[]): void {
    lessons.forEach((lesson) => {
      const personId = lesson?.teacher?.reference?.person?.id;
      if (personId && !this.teacherPhotoByPersonId.has(personId)) {
        this.loadTeacherPhoto(personId);
      }
    });
  }

  onPageChange($event: Pageable) {
    this.isLoading = true;
    this.getLessons($event).subscribe();
  }
}
